"use client";

import Link from "next/link";
import { useState } from "react";
import { motion } from "framer-motion";
import { BackgroundGradient } from "@/components/aceternity/background-gradient";
import { Button } from "@/components/ui/button";
import { Check, Minus } from "lucide-react";

const tiers = [
  {
    name: "Starter",
    monthlyPrice: 49,
    annualPrice: 39,
    description: "For solo founders doing their own outbound",
    features: {
      "Users": "1",
      "Contacts": "500",
      "AI Agents": "1",
      "Channels": "Email",
      "Active Campaigns": "3",
      "Approval Queue": true,
      "Unified Inbox": true,
      "Visual Workflow Builder": "Basic",
      "ICP Scoring": "Basic",
      "A/B Testing": true,
      "RBAC": false,
      "API Access": false,
      "Custom AI Models": false,
      "Support": "Email",
    },
    highlighted: false,
  },
  {
    name: "Growth",
    monthlyPrice: 149,
    annualPrice: 119,
    description: "For growing teams scaling outbound",
    features: {
      "Users": "5",
      "Contacts": "5,000",
      "AI Agents": "3",
      "Channels": "Email + LinkedIn",
      "Active Campaigns": "10",
      "Approval Queue": true,
      "Unified Inbox": true,
      "Visual Workflow Builder": "Full",
      "ICP Scoring": "Advanced",
      "A/B Testing": true,
      "RBAC": false,
      "API Access": true,
      "Custom AI Models": false,
      "Support": "Priority",
    },
    highlighted: true,
  },
  {
    name: "Scale",
    monthlyPrice: 399,
    annualPrice: 319,
    description: "For revenue teams and agencies",
    features: {
      "Users": "Unlimited",
      "Contacts": "25,000",
      "AI Agents": "Unlimited",
      "Channels": "All",
      "Active Campaigns": "Unlimited",
      "Approval Queue": true,
      "Unified Inbox": true,
      "Visual Workflow Builder": "Full + Custom",
      "ICP Scoring": "Advanced + Custom",
      "A/B Testing": true,
      "RBAC": true,
      "API Access": true,
      "Custom AI Models": true,
      "Support": "Dedicated CSM",
    },
    highlighted: false,
  },
];

const allFeatures = Object.keys(tiers[0].features);

function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check className="mx-auto h-5 w-5 text-green-500" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-muted-foreground/30" />;
  return <span className="text-sm text-foreground">{value}</span>;
}

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);

  return (
    <section className="px-4 py-24 sm:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            AI SDR Power. Startup-Friendly Pricing.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Get what others charge $5,000/month for. Start free, upgrade when you're ready.
          </p>

          {/* Annual Toggle */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <span className={`text-sm ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              className={`relative h-7 w-12 rounded-full transition-colors ${annual ? "bg-primary" : "bg-muted"}`}
              aria-label="Toggle annual billing"
            >
              <motion.div
                className="absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm"
                animate={{ left: annual ? "calc(100% - 1.625rem)" : "0.125rem" }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
            <span className={`text-sm ${annual ? "text-foreground" : "text-muted-foreground"}`}>
              Annual <span className="text-green-500">(save 20%)</span>
            </span>
          </div>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {tiers.map((tier, i) => {
            const price = annual ? tier.annualPrice : tier.monthlyPrice;
            const Card = tier.highlighted ? BackgroundGradient : "div";
            const cardProps = tier.highlighted
              ? { containerClassName: "h-full", className: "h-full rounded-[22px] bg-card p-6 sm:p-8" }
              : { className: "h-full rounded-2xl border border-border bg-card p-6 sm:p-8" };

            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card {...cardProps}>
                  <div className="flex h-full flex-col">
                    {tier.highlighted && (
                      <span className="mb-4 inline-block w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        Most Popular
                      </span>
                    )}
                    <h3 className="text-lg font-semibold">{tier.name}</h3>
                    <div className="mt-2">
                      <span className="text-4xl font-bold">${price}</span>
                      <span className="text-muted-foreground">/mo</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{tier.description}</p>

                    <ul className="mt-6 flex-1 space-y-3">
                      {Object.entries(tier.features).slice(0, 8).map(([feature, value]) => (
                        <li key={feature} className="flex items-center gap-2 text-sm">
                          {value === false ? (
                            <Minus className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                          ) : (
                            <Check className="h-4 w-4 shrink-0 text-green-500" />
                          )}
                          <span className={value === false ? "text-muted-foreground/50" : "text-foreground"}>
                            {typeof value === "string" ? `${value} ${feature.toLowerCase()}` : feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className="mt-8 w-full"
                      variant={tier.highlighted ? "default" : "outline"}
                      size="lg"
                      asChild
                    >
                      <Link href={tier.name === "Scale" ? "/demo" : "/auth/signup"}>
                        {tier.name === "Scale" ? "Book a Demo" : "Start Free Trial"}
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Enterprise */}
        <div className="mt-8 rounded-2xl border border-border bg-muted/30 p-8 text-center">
          <h3 className="text-lg font-semibold">Enterprise</h3>
          <p className="mt-2 text-muted-foreground">
            Need custom limits, dedicated infrastructure, or a SLA? Let&apos;s talk.
          </p>
          <Button className="mt-4" asChild>
            <Link href="/demo">Contact Sales</Link>
          </Button>
        </div>

        {/* Full Comparison Table */}
        <div className="mt-20">
          <h2 className="mb-8 text-center text-2xl font-bold">Full Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 text-left text-sm font-medium text-muted-foreground">Feature</th>
                  {tiers.map((tier) => (
                    <th key={tier.name} className="px-6 py-3 text-center text-sm font-medium">
                      <span className={tier.highlighted ? "text-primary" : "text-foreground"}>
                        {tier.name}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((feature, i) => (
                  <tr key={feature} className={`border-b border-border/50 ${i % 2 === 0 ? "bg-muted/10" : ""}`}>
                    <td className="py-3 text-sm text-foreground">{feature}</td>
                    {tiers.map((tier) => (
                      <td key={tier.name} className="px-6 py-3 text-center">
                        <FeatureValue value={tier.features[feature as keyof typeof tier.features]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="mx-auto mt-20 max-w-3xl">
          <h2 className="mb-8 text-center text-2xl font-bold">Pricing FAQ</h2>
          <div className="space-y-4">
            {[
              { q: "Is there a free trial?", a: "Yes! All plans include a 14-day free trial. No credit card required to start." },
              { q: "Can I switch plans?", a: "Absolutely. Upgrade or downgrade anytime. Changes take effect at your next billing cycle." },
              { q: "What happens if I exceed my contact limit?", a: "We'll notify you and suggest upgrading. We never delete your data or stop your campaigns without warning." },
              { q: "Do you offer discounts for startups?", a: "Yes. Early-stage startups (pre-Series A) may qualify for additional discounts. Contact us to learn more." },
              { q: "Is there a setup fee?", a: "No. Zero setup fees, zero implementation costs, zero hidden charges. The price you see is the price you pay." },
            ].map((item) => (
              <details key={item.q} className="group rounded-xl border border-border bg-card">
                <summary className="flex cursor-pointer items-center justify-between p-5 text-sm font-medium text-foreground">
                  {item.q}
                  <svg className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="border-t border-border px-5 pb-5 pt-4 text-sm text-muted-foreground">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
